import { Injectable } from '@angular/core';

import { CoordinatesModel } from '../models/coordinates.model';
import { CoordinatesHelper } from '../helpers/coordinates/coordinates.helper';
import { WatchedElementModel } from '../models/watched-element.model';
import { dimensionsEnum } from '../enums/dimensions.enum';
import * as _ from 'underscore';

@Injectable()
export class WatchCoordinatesService {

    private watchedElements: WatchedElementModel[]  = [];
    private watchInterval: NodeJS.Timeout;
    private watchFrequency: number = 500;

    /**
     * Starts watching an individual element for coordinate changes
    */
    public watchElement(element: HTMLElement): WatchedElementModel {
        const watchedElement: WatchedElementModel = new WatchedElementModel(element);
        const isWatchingElements: boolean = this.watchInterval !== null && typeof this.watchInterval === 'object';

        // Adds the new watched element to the queue
        this.watchedElements.push(watchedElement);

        // If the watch interval isn't already running. start running it
        if (!isWatchingElements) {
            this.startWatching();
        }

        return watchedElement;
    }

    /**
     * Stops watching an element for coordinate changes
     * and removes it from the queue
    */
    public ignoreElement(watchedElement: WatchedElementModel) {
        const remainingElements: WatchedElementModel[] = _.without(this.watchedElements, watchedElement);
        const hasAnyWatchedElements: boolean = remainingElements.length > 0;

        this.watchedElements = remainingElements;

        watchedElement.unsubscribe();

        // If there are no elements left to watch, stop watching
        if (!hasAnyWatchedElements) {
            this.stopWatching();
        }
    }

    /**
     * Starts watching all provided elements
     * for coordinate changes
    */
    private startWatching(): void {
        this.watchInterval = setInterval(
            () => this.checkAllElements(),
            this.watchFrequency
        );
    }

    /**
     * Stops watching any elements
    */
    private stopWatching(): void {
        clearInterval(this.watchInterval);

        this.watchInterval = null;
    }

    /**
     * Checks an individual watched element for coordinate changes
    */
    private checkElement(watchedElement: WatchedElementModel): void {
        const coordinates: CoordinatesModel = CoordinatesHelper.getElementCoordinates(watchedElement.element);
        const previousCoordinates: CoordinatesModel = watchedElement.previousCoordinates;
        let isChanged: boolean = false;

        if (previousCoordinates) {
            for (const key of Object.keys(coordinates)) {
                if (previousCoordinates.hasOwnProperty(key)) {
                    isChanged = coordinates[key] !== previousCoordinates[key];

                    if (isChanged) {
                        const heightChanged: boolean = key === dimensionsEnum.height;
                        const widthChanged: boolean = key === dimensionsEnum.width;
                        const wasResized: boolean = heightChanged || widthChanged;

                        watchedElement.change.next(coordinates);

                        if (wasResized) {
                            watchedElement.resize.next(coordinates);
                        }

                        break;
                    }
                }
            }

        } else {
            watchedElement.resize.next(coordinates);
            watchedElement.change.next(coordinates);
        }

        watchedElement.previousCoordinates = coordinates;
    }

    /**
     * Checks for changes in the coordinates
     * of all watched elements
    */
    private checkAllElements(): void {
        this.watchedElements.forEach(
            element => this.checkElement(element)
        );
    }

}
